<template>
  <div class="qfs">
    <div class="qfs-header">
      <p class="qfs-subtitle">
        Mahalla jamg'armasi bildirishnomalariga ulangan foydalanuvchilar.
        Ro'yxatga kamida bitta hodisani yoqqanlar tushadi.
      </p>
      <el-button
        v-if="isAdmin"
        type="primary"
        :loading="exporting"
        @click="exportExcel"
      >
        <el-icon v-if="!exporting" style="margin-right: 4px"><Download /></el-icon>
        Excel'ga yuklab olish
      </el-button>
    </div>

    <div class="qfs-note">
      <el-icon><InfoFilled /></el-icon>
      <span>
        Ro'yxatda shaxsiy ma'lumot (F.I.O., telefon raqami) bor — faqat admin
        ko'radi va eksport qiladi.
      </span>
    </div>

    <div class="qfs-filters">
      <el-select
        v-model="regionId"
        placeholder="Viloyat"
        clearable
        filterable
        :loading="regionsLoading"
        class="qfs-filters__select"
        @change="onRegionChange"
      >
        <el-option
          v-for="region in regions"
          :key="region.id"
          :value="region.id"
          :label="region.title"
        />
      </el-select>

      <el-select
        v-model="districtId"
        placeholder="Tuman"
        clearable
        filterable
        :loading="districtsLoading"
        class="qfs-filters__select"
        @change="onDistrictChange"
      >
        <el-option
          v-for="district in districts"
          :key="district.id"
          :value="district.id"
          :label="district.title"
        />
      </el-select>

      <el-select
        v-model="quarterId"
        :placeholder="districtId ? 'Mahalla' : 'Avval tumanni tanlang'"
        :disabled="!districtId"
        clearable
        filterable
        :loading="quartersLoading"
        class="qfs-filters__select"
        @change="onFilterChange"
      >
        <el-option
          v-for="quarter in quarters"
          :key="quarter.id"
          :value="quarter.id"
          :label="quarter.title"
        />
      </el-select>

      <el-input
        v-model="search"
        placeholder="F.I.O. yoki telefon"
        clearable
        class="qfs-filters__search"
        @keyup.enter="onFilterChange"
        @clear="onFilterChange"
      >
        <template #append>
          <el-button @click="onFilterChange">
            <el-icon><Search /></el-icon>
          </el-button>
        </template>
      </el-input>

      <el-button v-if="hasActiveFilters" @click="resetFilters">Tozalash</el-button>
    </div>

    <div v-if="errorMessage" class="qfs-state">
      <el-result icon="error" title="Ro'yxatni yuklab bo'lmadi" :sub-title="errorMessage">
        <template #extra>
          <el-button type="primary" @click="fetchSubscribers">
            <el-icon style="margin-right: 4px"><Refresh /></el-icon>
            Qayta urinish
          </el-button>
        </template>
      </el-result>
    </div>

    <div v-else-if="loading" class="qfs-state">
      <el-skeleton :rows="8" animated />
    </div>

    <div v-else-if="!subscribers.length" class="qfs-state">
      <MfEmpty
        title="Obunachi topilmadi"
        :description="
          hasActiveFilters
            ? 'Filtrlarni o\'zgartirib ko\'ring'
            : 'Hali hech kim bildirishnomalarni yoqmagan'
        "
      />
      <div v-if="hasActiveFilters" class="qfs-state__actions">
        <el-button @click="resetFilters">Filtrlarni tozalash</el-button>
      </div>
    </div>

    <template v-else>
      <el-table :data="subscribers" border stripe class="qfs-table">
        <el-table-column type="index" :index="rowIndex" width="60" align="center" />
        <el-table-column label="F.I.O." min-width="200">
          <template #default="scope">
            {{ scope.row.fullname || "—" }}
          </template>
        </el-table-column>
        <el-table-column label="Telefon" width="180">
          <template #default="scope">
            <div class="qfs-phone">
              <span>{{ phone(scope.row.phone_number) }}</span>
              <el-tooltip placement="top" :content="verifiedHint(scope.row.phone_verified)">
                <el-tag size="small" effect="plain" type="info">
                  {{ scope.row.phone_verified ? "tasdiqlangan" : "tasdiqlanmagan" }}
                </el-tag>
              </el-tooltip>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="Mahalla" min-width="160">
          <template #default="scope">
            <span v-if="scope.row.quarter_title">{{ scope.row.quarter_title }}</span>
            <el-tag v-else type="warning" size="small" effect="plain">
              Biriktirilmagan
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="Tuman" min-width="140">
          <template #default="scope">{{ scope.row.district_title || "—" }}</template>
        </el-table-column>
        <el-table-column label="Viloyat" min-width="140">
          <template #default="scope">{{ scope.row.region_title || "—" }}</template>
        </el-table-column>
        <el-table-column label="Hodisalar" width="230" align="center">
          <template #default="scope">
            <el-tag
              v-for="event in eventFlags"
              :key="event.field"
              size="small"
              effect="plain"
              class="qfs-chip"
              :type="scope.row[event.field] ? 'success' : 'info'"
            >
              {{ event.label }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="Kanallar" width="200" align="center">
          <template #default="scope">
            <el-tag
              v-for="channel in channelFlags"
              :key="channel.field"
              size="small"
              effect="plain"
              class="qfs-chip"
              :type="scope.row[channel.field] ? 'success' : 'info'"
            >
              {{ channel.label }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="Yangilangan" width="150">
          <template #default="scope">{{ formatDate(scope.row.updated_at) }}</template>
        </el-table-column>
      </el-table>
    </template>

    <el-pagination
      v-if="!errorMessage && count > 0"
      background
      layout="sizes, prev, pager, next, total"
      :total="count"
      :page-sizes="[20, 50, 100, 200]"
      v-model:page-size="limit"
      v-model:current-page="page"
      class="qfs-pagination"
      @size-change="onSizeChange"
      @current-change="scheduleListFetch"
    />
  </div>
</template>

<script>
import { ElMessage, ElNotification } from "element-plus";
import { Download, InfoFilled, Refresh, Search } from "@element-plus/icons-vue";
import MfEmpty from "@/components/core/MfEmpty.vue";
import { filenameFromResponse, saveBlob } from "@/utils/common";
import api, {
  DEFAULT_LIMIT,
  apiErrorMessage,
  formatPhone,
  loadDistricts,
  loadQuarters,
  loadRegions,
  normalizeSearch,
} from "./quarterFundNotificationsApi";

const EVENT_FLAGS = [
  { field: "notify_monthly_balance", label: "Qoldiq" },
  { field: "notify_income", label: "Tushum" },
  { field: "notify_outcome", label: "Xarajat" },
];

const CHANNEL_FLAGS = [
  { field: "channel_sms", label: "SMS" },
  { field: "channel_push", label: "Push" },
  { field: "channel_inapp", label: "Ilova" },
];

export default {
  name: "QuarterFundSubscribers",
  components: { MfEmpty, Download, InfoFilled, Refresh, Search },
  data() {
    return {
      loading: true,
      exporting: false,
      errorMessage: "",
      subscribers: [],
      count: 0,
      page: 1,
      limit: DEFAULT_LIMIT,
      // Eskirgan javob yangisini bosib ketmasligi uchun.
      listRequestId: 0,
      fetchQueued: false,

      regions: [],
      districts: [],
      quarters: [],
      regionsLoading: false,
      districtsLoading: false,
      quartersLoading: false,

      regionId: null,
      districtId: null,
      quarterId: null,
      search: "",

      eventFlags: EVENT_FLAGS,
      channelFlags: CHANNEL_FLAGS,
    };
  },
  computed: {
    isAdmin() {
      return this.$store.state.role === "admin";
    },
    hasActiveFilters() {
      return !!(this.regionId || this.districtId || this.quarterId || this.search);
    },
  },
  mounted() {
    this.$store.commit("updateHeaderTitle", "Jamg'arma obunachilari");
    this.fetchRegions();
    this.fetchSubscribers();
  },
  methods: {
    phone(value) {
      return formatPhone(value);
    },
    // `phone_verified` faqat ma'lumot uchun — SMS yuborilishiga ta'sir qilmaydi.
    verifiedHint(verified) {
      return verified
        ? "Faqat ma'lumot uchun — SMS yuborilishiga ta'sir qilmaydi"
        : "Faqat ma'lumot uchun — bu obunachi ham SMS oladi";
    },
    formatDate(value) {
      if (!value) return "—";
      return this.$moment(value).format("DD.MM.YYYY HH:mm");
    },
    rowIndex(index) {
      return this.limit * (this.page - 1) + index + 1;
    },

    // ---- hudud filtrlari ----
    fetchRegions() {
      this.regionsLoading = true;
      loadRegions()
        .then((regions) => {
          this.regions = regions;
        })
        .catch(() => {
          ElMessage.warning("Viloyatlar ro'yxatini yuklab bo'lmadi");
        })
        .finally(() => {
          this.regionsLoading = false;
        });
    },
    fetchDistricts() {
      this.districtsLoading = true;
      loadDistricts(this.regionId)
        .then((districts) => {
          this.districts = districts;
        })
        .catch(() => {
          ElMessage.warning("Tumanlar ro'yxatini yuklab bo'lmadi");
        })
        .finally(() => {
          this.districtsLoading = false;
        });
    },
    // Mahallalar ~10 000 ta — faqat tuman tanlanganda yuklaymiz.
    fetchQuarters() {
      this.quartersLoading = true;
      loadQuarters({ regionId: this.regionId, districtId: this.districtId })
        .then((quarters) => {
          this.quarters = quarters;
        })
        .catch(() => {
          ElMessage.warning("Mahallalar ro'yxatini yuklab bo'lmadi");
        })
        .finally(() => {
          this.quartersLoading = false;
        });
    },
    onRegionChange() {
      this.districtId = null;
      this.quarterId = null;
      this.districts = [];
      this.quarters = [];
      if (this.regionId) this.fetchDistricts();
      this.onFilterChange();
    },
    onDistrictChange() {
      this.quarterId = null;
      this.quarters = [];
      if (this.districtId) this.fetchQuarters();
      this.onFilterChange();
    },
    onFilterChange() {
      this.page = 1;
      this.fetchSubscribers();
    },
    resetFilters() {
      this.regionId = null;
      this.districtId = null;
      this.quarterId = null;
      this.districts = [];
      this.quarters = [];
      this.search = "";
      this.onFilterChange();
    },
    onSizeChange() {
      this.page = 1;
      this.scheduleListFetch();
    },
    // Hajm o'zgarganda el-pagination `size-change` dan keyin `current-change`
    // ni ham emit qilishi mumkin — ikkala hodisa bitta so'rovga yig'iladi.
    scheduleListFetch() {
      if (this.fetchQueued) return;
      this.fetchQueued = true;
      this.$nextTick(() => {
        this.fetchQueued = false;
        this.fetchSubscribers();
      });
    },

    // ---- ro'yxat ----
    // Bo'sh filtr yuborilmaydi: backendda default 0 = filtr yo'q.
    buildFilterParams() {
      const params = {};
      if (this.regionId) params.region_id = this.regionId;
      if (this.districtId) params.district_id = this.districtId;
      if (this.quarterId) params.quarter_id = this.quarterId;
      const search = normalizeSearch(this.search);
      if (search) params.search = search;
      return params;
    },
    fetchSubscribers() {
      const requestId = ++this.listRequestId;
      const isStale = () => requestId !== this.listRequestId;
      this.loading = true;
      this.errorMessage = "";
      return api
        .subscribers({
          ...this.buildFilterParams(),
          limit: this.limit,
          offset: this.limit * (this.page - 1),
        })
        .then(({ data }) => {
          if (isStale()) return;
          this.subscribers = data?.subscribers || [];
          // count — filtr bo'yicha jami, sahifadagi emas.
          this.count = data?.count || 0;
          this.errorMessage = "";
        })
        .catch(async (error) => {
          const message = await apiErrorMessage(error);
          if (isStale()) return;
          this.errorMessage = message;
        })
        .finally(() => {
          if (!isStale()) this.loading = false;
        });
    },
    exportExcel() {
      if (this.exporting) return;
      this.exporting = true;
      // limit/offset yuborilmaydi — eksportda filtr bo'yicha to'liq ro'yxat.
      api
        .subscribersExcel(this.buildFilterParams())
        .then((response) => {
          const fallback = `Obunachilar-${this.$moment().format("YYYY-MM-DD__HH-mm")}.xlsx`;
          saveBlob(response.data, filenameFromResponse(response.headers, fallback));
          ElMessage.success("Excel fayl yuklab olindi");
        })
        .catch(async (error) => {
          ElNotification.error({
            title: "Eksport qilinmadi",
            message: await apiErrorMessage(error, "Faylni yuklab olishda xatolik"),
          });
        })
        .finally(() => {
          this.exporting = false;
        });
    },
  },
};
</script>

<style scoped>
.qfs {
  padding: 4px 0;
}
.qfs-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.qfs-subtitle {
  margin: 0;
  max-width: 720px;
  font-size: 13px;
  line-height: 1.5;
  color: #909399;
}
.qfs-note {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 14px 0;
  font-size: 12px;
  color: #909399;
}
.qfs-filters {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
  flex-wrap: wrap;
}
.qfs-filters__select {
  width: 200px;
}
.qfs-filters__search {
  width: 280px;
}
.qfs-state {
  padding: 12px 0;
}
.qfs-state__actions {
  text-align: center;
}
.qfs-phone {
  display: flex;
  flex-direction: column;
  gap: 3px;
  align-items: flex-start;
}
.qfs-chip {
  margin: 2px;
}
.qfs-pagination {
  margin-top: 16px;
  justify-content: flex-end;
}

@media (max-width: 768px) {
  .qfs-filters__select,
  .qfs-filters__search {
    width: 100%;
  }
  .qfs-header .el-button {
    width: 100%;
    min-height: 44px;
  }
}
</style>
